// src/pages/Login.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Full-screen login page.
//  - Parallax-style background image (Unsplash CDN)
//  - Glassmorphism card overlay
//  - Ant Design Form with validation
//  - Redirects to /dashboard if already logged in
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect } from "react";
import { Form, Input, Button, Typography, Alert, Divider } from "antd";
import { UserOutlined, LockOutlined, CrownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const { Title, Text } = Typography;

const Login = () => {
  const { login, authLoading, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [error, setError] = React.useState("");

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const onFinish = async ({ email, password }) => {
    setError("");
    const result = await login(email, password);
    console.log(result);
    if (result.success) {
      navigate("/dashboard", { replace: true });
    } else {
      setError(result.message);
    }
  };

  return (
    <div style={styles.page}>
      {/* Dark gradient overlay on top of the BG image */}
      <div style={styles.overlay} />

      {/* Centered glass card */}
      <div style={styles.card}>
        {/* Brand */}
        {/* <div style={styles.brand}>
          <CrownOutlined style={{ fontSize: 32, color: "black" }} />
          <Title level={3} style={{ color: "black", margin: 0 }}>
            AuthApp
          </Title>
        </div> */}

        <Title
          level={4}
          style={{ color: "black", textAlign: "center", marginBottom: 4 }}
        >
          Welcome back
        </Title>
        <Text
          style={{
            color: "black",
            display: "block",
            textAlign: "center",
            marginBottom: 28,
          }}
        >
          Sign in to your account
        </Text>

        {/* Error banner */}
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError("")}
            style={{ marginBottom: 20, borderRadius: 8 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          size="large"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Enter a valid email address" },
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: "black" }} />}
              placeholder="Email address"
              style={styles.input}
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: "Please enter your password" }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "black" }} />}
              placeholder="Password"
              style={styles.input}
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={authLoading}
              style={styles.submitBtn}
            >
              {authLoading ? "Signing in…" : "Sign In"}
            </Button>
          </Form.Item>
        </Form>

        <Divider style={{ borderColor: "#334155" }} />

        {/* Demo credentials hint */}
        {/* <div style={{ textAlign: "center" }}>
          <Text style={{ color: "black", fontSize: 12 }}>
            Demo: admin@example.com / Admin@123
          </Text>
        </div> */}
      </div>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  page: {
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // High-res background from Unsplash (city lights / abstract)
    backgroundImage:
      "url(https://res.cloudinary.com/tariqtvsl/image/upload/v1779084339/CaseImage/WhatsApp_Image_2026-05-18_at_12.02.50_PM_kivm60.jpg)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
  },
  overlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(135deg, rgba(15,23,42,0.85) 0%, rgba(30,27,75,0.80) 100%)",
    backdropFilter: "blur(2px)",
  },
  card: {
    position: "relative", // above overlay
    zIndex: 10,
    width: "100%",
    maxWidth: 420,
    padding: "40px 36px",
    borderRadius: 16,
    color: "black",
    background: "rgba(255, 255, 255, 0.75)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(99, 102, 241, 0.25)",
    boxShadow: "0 25px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)",
    margin: "0 16px",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginBottom: 24,
  },
  input: {
    background: "rgba(255, 255, 255, 0.8)",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "black",
  },
  submitBtn: {
    height: 46,
    borderRadius: 8,
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    border: "none",
    fontWeight: 600,
    letterSpacing: 0.5,
    fontSize: 15,
    marginTop: 4,
  },
};

export default Login;
